<template>
  <div class="list-header relative">
    <!-- List Name -->
    <div
      class="text-white text-chplus-regular px-8"
      :style="{
        background:
          headerStyle === 'normal' ? 'var(--contrast-dark)' : 'transparent'
      }"
      @click="$emit('header-clicked')"
    >
      <span
        v-if="!allowNamechange && list.adapted == null"
        class="block py-2"
        style="line-height: 1.1em;"
        v-html="listName"
      ></span>
      <span
        v-else-if="!allowNamechange"
        class="block py-2"
        style="line-height: 1.1em;"
        >{{ listName }}</span
      >
      <input
        v-else
        type="text"
        class="mr-2 py-0 bg-transparent text-chplus-regular text-center w-full"
        @blur="onNameChanged"
        v-model="listName"
      />
    </div>
    <div class="absolute top-0 right-0 text-white mr-2">
      <slot name="action-right"></slot>
    </div>
    <!-- List Info -->
    <div
      :style="{
        backgroundImage: colorGradient,
        height: '1.5rem'
      }"
      :class="{
        'rounded-t-lg': headerStyle === 'clear'
      }"
    />
    <div
      class="party-percentages-area flex flex-row justify-between text-chplus-s text-white bg-interaction-dark"
      style="height: 1.25rem;"
      :style="{ 'margin-top': '-1.25rem' }"
      :class="{
        'rounded-t-lg': headerStyle === 'clear'
      }"
    >
      <div
        v-for="[party, percent] in this.getListParties(
          list.candidates.map(entry => entry.id)
        )"
        :key="party.id"
        class="mx-2 flex flex-col justify-center"
        style="line-height: 1em;"
        :class="{ 'flex-1': listPartyCount !== 2 }"
      >
        <span
          class="relative whitespace-no-wrap"
          style="top:-0.033rem; text-overflow: ellipsis; overflow: hidden; padding-bottom: 0.25em;margin-bottom: -0.25em;"
          >{{ party.abbr }} {{ Math.round(percent * 100 || 0) }}%</span
        >
      </div>
    </div>
    <div
      class="list-preview"
      :class="{ 'mt-3': !allowNamechange, 'mt-1': allowNamechange }"
    >
      <div class="flex items-stretch justify-center">
        <table class="table-fixed w-full lg:max-w-md">
          <tr class="text-chplus-xs leading-tight">
            <td class="px-2 py-0 text-left">
              {{ $t("components.list.list.popup_list_summary_age") }}
            </td>
            <td class="px-2 py-0 text-center">
              {{ $t("components.list.list.popup_list_summary_gender") }}
            </td>
            <td class="px-2 py-0 text-right">
              {{ $t("components.list.list.popup_list_summary_places") }}
            </td>
          </tr>
          <tr class="text-chplus-s leading-none">
            <td class="px-2 py-0 text-left">
              <average-age :people="candidates" />
            </td>
            <td class="px-2 py-0 text-center">
              <gender-distribution :people="candidates" />
            </td>
            <td class="text-chplus-regular px-2 py-0 text-right">
              {{ numCandidates }} / {{ activeElection.seats }}
            </td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { PartyColor } from "@/utils/PartyColor";
import { ListParties } from "@/utils/ListParties";
import AverageAge from "@/components/list/AverageAge";
import GenderDistribution from "@/components/list/GenderDistribution";

export default {
  name: "ListHeader",
  mixins: [PartyColor, ListParties],
  components: {
    AverageAge,
    GenderDistribution
  },
  props: {
    list: {
      type: Object,
      required: true
    },
    allowNamechange: {
      type: Boolean,
      default: false
    },
    headerStyle: {
      type: String,
      default: "normal"
    }
  },
  data: () => {
    return {
      listName: ""
    };
  },
  created() {
    const number = this.list.number;
    if (number > 0 && this.list.adapted < 1) {
      this.listName = this.$t("components.list.name.official", {
        n: number,
        name: this.list.name
      });
    } else {
      this.listName = this.list.name;
    }
  },
  computed: {
    ...mapGetters("candidate", { getCandidate: "candidate" }),
    ...mapGetters("election", ["activeElection"]),
    candidates() {
      return this.list.candidates.map(item => {
        return this.getCandidate(item.id);
      });
    },
    numCandidates() {
      return this.list.candidates.length;
    },
    listPartyCount() {
      return this.getListPartyCount(
        this.list.candidates.map(entry => entry.id)
      );
    },
    colorGradient() {
      if (this.list.candidates.length === 0) {
        return `linear-gradient(to right, #888 0%, #888 100%)`;
      }
      // Create a map of party => count
      const entries = this.getListParties(
        this.list.candidates.map(entry => entry.id)
      );
      const startPosition = entries[0][1];
      const length = startPosition < 1 ? 1 - startPosition : 1;
      let position = 0;
      let colors = entries.map(e => {
        const party = e[0];
        const percentage = ((position + e[1] - startPosition) / length) * 100;
        const color = this.getPartyColor(party.id);

        position += e[1];
        return `${color} ${percentage}%`;
      });

      if (colors.length === 0) {
        // Fall back to white if no parties
        colors = ["#FFF", "#FFF"];
      } else if (colors.length === 1) {
        // Gradient requires at least 2 colors
        colors.push(colors[0]);
      }

      return `linear-gradient(to right, ${colors.join(", ")})`;
    }
  },
  methods: {
    onNameChanged(event) {
      this.$emit("namechange", event.target.value);
    }
  }
};
</script>

<style scoped>
input {
  -webkit-user-select: text; /* Chrome, Opera, Safari */
  -moz-user-select: text; /* Firefox 2+ */
  -ms-user-select: text; /* IE 10+ */
  user-select: text; /* Standard syntax */
}
</style>
